// API Configuration and Base Setup
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://localhost:7269/api';

export interface ApiResponse<T = any> {
  isSuccess: boolean;
  message: string;
  data?: T;
  errors?: any;
  id?: number;
  total?: number;
}

export interface LoginRequest {
  userName: string;
  password: string;
  userType?: string;
  remember?: boolean;
}

export interface LoginResponse {
  userId: number;
  personName: string;
  userType: string;
  refId: number;
  isActive: boolean;
  token: string;
  userPermissionList?: UserModulePermission[];
}

export interface UserModulePermission {
  moduleId?: number;
  moduleName?: string;
  isView?: boolean;
  isAdd?: boolean;
  isEdit?: boolean;
  isVoid?: boolean;
  isExport?: boolean;
}

export interface DashboardMetrics {
  totalParties: number;
  totalPartiesActive: number;
  internalAccounts: number;
  totalTokens: number;
  todayBuy: number;
  todayBuyTransactions: number;
  todaySell: number;
  todaySellTransactions: number;
  todaySwaps: number;
}

export interface RecentTransaction {
  id: string;
  partyName: string;
  transactionDate: string;
  transactionType: string;
  amount: number;
}

export interface RecentActivity {
  id: string;
  remarks: string;
  creatorName: string;
  createdDate:string;
}

// Helper function to get auth token
export const getAuthToken = (): string | null => {
  const auth = sessionStorage.getItem('auth');
  if (auth) {
    try {
      const authData = JSON.parse(auth);
      console.log('Auth data:', { isAuthenticated: authData.isAuthenticated, hasUser: !!authData.user, hasToken: !!authData.user?.token });
      return authData.user?.token || null;
    } catch (e) {
      console.error('Failed to parse auth data:', e);
      return null;
    }
  }
  console.log('No auth data in sessionStorage');
  return null;
};

// Helper function to set auth headers
const getHeaders = (includeAuth = true): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (includeAuth) {
    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return headers;
};

// Generic fetch wrapper
export const apiCall = async <T = any>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  body?: any,
  includeAuth = true
): Promise<ApiResponse<T>> => {
  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers = getHeaders(includeAuth);
    const options: RequestInit = {
      method,
      headers,
    };

    if (body && (method === 'POST' || method === 'PUT')) {
      options.body = JSON.stringify(body);
    }

    const authHeader = (headers as Record<string, string>)['Authorization'];
    console.log(`API Call: ${method} ${endpoint}`, { 
      hasAuth: includeAuth, 
      hasToken: !!authHeader,
      authHeader: authHeader ? `${authHeader.substring(0, 30)}...` : 'None'
    });

    // Log complete request payload for POST/PUT requests
    if (body && (method === 'POST' || method === 'PUT')) {
      console.log(`📤 Request Payload for ${endpoint}:`, body);
      console.table(body);
    }

    const response = await fetch(url, options);

    if (!response.ok) {
      console.error(`API Error: ${response.status} ${response.statusText}`, { endpoint, method });
      if (response.status === 401) {
        // Unauthorized - clear auth and redirect to login
        console.warn('401 Unauthorized - clearing auth and redirecting to login');
        sessionStorage.removeItem('auth');
        window.location.href = '/login';
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: ApiResponse<T> = await response.json();
    return data;
  } catch (error) {
    console.error('API call failed:', error);
    return {
      isSuccess: false,
      message: error instanceof Error ? error.message : 'An error occurred',
    };
  }
};

// Login API
export const loginAPI = async (credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> => {
  return apiCall<LoginResponse>('/login/login', 'POST', credentials, false);
};

// Logout API
export const logoutAPI = async (): Promise<ApiResponse> => {
  return apiCall('/login/logout', 'POST', {}, true);
};

// Dashboard Metrics API
export const getDashboardMetricsAPI = async (): Promise<ApiResponse<DashboardMetrics>> => {
  return apiCall<DashboardMetrics>('/dashboard/metrics', 'GET', undefined, true);
};

// Recent Transactions API
export const getRecentTransactionsAPI = async (params?: { pageNo?: number; pageSize?: number;searchText?: string;isActive?: boolean }): Promise<ApiResponse<RecentTransaction[]>> => {
  return apiCall<RecentTransaction[]>('/ManageReport/GetRecentTransaction', 'POST', params || { pageNo: 1, pageSize: 10 }, true);
};

// Recent Activity API
export const getRecentActivityAPI = async (params?: { pageNo?: number; pageSize?: number;searchText?: string;isActive?: boolean; actionType?:string; entityType?:string }): Promise<ApiResponse<RecentActivity[]>> => {
  return apiCall<RecentActivity[]>('/User/GetAuditLogList', 'POST', params || { pageNo: 1, pageSize: 10 }, true);
};
